/**
 * Textarea Component — HMVMReviewer Design System Phase 1
 * 
 * Features:
 * - Multi-line text input
 * - Label and error message support
 * - Character count support
 * - Auto-resize option
 * - Full accessibility (ARIA, required indicator)
 * - Uses semantic theme tokens
 * - Square geometry (border-radius: 0)
 */

import { TextareaHTMLAttributes, useRef, useEffect } from 'react';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  showCharCount?: boolean;
  autoResize?: boolean;
}

export function Textarea({
  label,
  error,
  helperText,
  fullWidth = false,
  showCharCount = false,
  autoResize = false,
  className = '',
  id,
  required,
  disabled,
  maxLength,
  value,
  ...props
}: TextareaProps) {
  const textareaId = id || `textarea-${Math.random().toString(36).slice(2, 11)}`;
  const errorId = error ? `${textareaId}-error` : undefined;
  const helperId = helperText ? `${textareaId}-helper` : undefined;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const charCount = value ? String(value).length : 0;

  useEffect(() => {
    if (autoResize && textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [value, autoResize]);

  return (
    <div className={`flex flex-col gap-1.5 ${fullWidth ? 'w-full' : ''}`}>
      <div className="flex justify-between items-center">
        {label && (
          <label
            htmlFor={textareaId}
            className="text-[var(--reviewer-text)] text-[var(--reviewer-text-sm)] font-medium font-mono"
          >
            {label}
            {required && (
              <span className="text-[var(--reviewer-danger)] ml-1" aria-label="required">
                *
              </span>
            )}
          </label>
        )}
        {showCharCount && maxLength && (
          <span
            className={`text-[var(--reviewer-text-xs)] font-mono ${
              charCount > maxLength ? 'text-[var(--reviewer-danger)]' : 'text-[var(--reviewer-text-secondary)]'
            }`}
          >
            {charCount} / {maxLength}
          </span>
        )}
      </div>
      
      <textarea
        ref={textareaRef}
        id={textareaId}
        disabled={disabled}
        required={required}
        maxLength={maxLength}
        value={value}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={[errorId, helperId].filter(Boolean).join(' ') || undefined}
        className={`
          w-full px-3 py-2
          bg-[var(--reviewer-input-bg)]
          border border-[var(--reviewer-input-border)]
          text-[var(--reviewer-text)]
          font-mono text-[var(--reviewer-text-base)]
          transition-all duration-150
          focus:outline-none
          focus:border-[var(--reviewer-input-focus-border)]
          focus:ring-2 focus:ring-[var(--reviewer-input-focus-ring)]
          disabled:opacity-50 disabled:cursor-not-allowed
          ${autoResize ? 'resize-none overflow-hidden' : 'resize-y'}
          ${error ? 'border-[var(--reviewer-danger-border)] focus:border-[var(--reviewer-danger)] focus:ring-[var(--reviewer-danger-soft)]' : ''}
          ${className}
        `}
        {...props}
      />
      
      {error && (
        <p
          id={errorId}
          className="text-[var(--reviewer-danger)] text-[var(--reviewer-text-xs)] font-mono"
          role="alert"
        >
          {error}
        </p>
      )}
      
      {helperText && !error && (
        <p
          id={helperId}
          className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-xs)] font-mono"
        >
          {helperText}
        </p>
      )}
    </div>
  );
}
